import { useCallback, useEffect, useRef, useState } from 'react';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AssistantChatState {
  messages: ChatMessage[];
  input: string;
  setInput: (v: string) => void;
  streaming: boolean;
  error: string | null;
  send: (text: string) => void;
}

/**
 * Shared SSE chat driver for the AI assistants (ticket + sales).
 *
 * Posts `{ ...body(), messages }` to `endpoint` and appends the streamed `{delta}` frames to
 * the trailing assistant message. All entity context is assembled server-side — `body()` only
 * supplies the scope (which ticket / which opportunity / which pipeline), never the data.
 *
 * `resetKey` clears the conversation when the subject changes (e.g. navigating to another
 * ticket or opportunity), so replies never carry over from a different record.
 */
export function useAssistantStream(opts: {
  endpoint: string;
  body: () => Record<string, unknown>;
  resetKey?: unknown;
}): AssistantChatState {
  const { endpoint, resetKey } = opts;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `body` is typically an inline arrow, so it changes identity every render — hold the latest
  // in a ref rather than making it a dependency of `send` (which would re-create the callback
  // on every render and defeat the memoisation).
  const bodyRef = useRef(opts.body);
  bodyRef.current = opts.body;

  // Mirrors of the live state so `send` can read them without listing them as deps.
  const messagesRef = useRef<ChatMessage[]>(messages);
  messagesRef.current = messages;
  const streamingRef = useRef(false);

  useEffect(() => {
    setMessages([]);
    setInput('');
    setError(null);
  }, [resetKey]);

  const send = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || streamingRef.current) return;
    setError(null);
    const history: ChatMessage[] = [...messagesRef.current, { role: 'user', content: trimmed }];
    setMessages([...history, { role: 'assistant', content: '' }]);
    setInput('');
    streamingRef.current = true;
    setStreaming(true);

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...bodyRef.current(), messages: history }),
      });
      if (!res.ok || !res.body) {
        let msg = `Request failed (${res.status})`;
        try { msg = (await res.json())?.error?.message ?? msg; } catch { /* non-JSON */ }
        throw new Error(msg);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() ?? '';
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          const data = line.slice(5).trim();
          if (data === '[DONE]') continue;
          try {
            const obj = JSON.parse(data) as { delta?: string; error?: string };
            if (obj.error) {
              setError(obj.error);
            } else if (obj.delta) {
              setMessages((prev) => {
                const copy = [...prev];
                const last = copy[copy.length - 1];
                if (last?.role === 'assistant') copy[copy.length - 1] = { ...last, content: last.content + obj.delta };
                return copy;
              });
            }
          } catch { /* ignore malformed chunk */ }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      // Drop the empty assistant placeholder so the thread doesn't show a blank bubble.
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last.content === '') return prev.slice(0, -1);
        return prev;
      });
    } finally {
      streamingRef.current = false;
      setStreaming(false);
    }
  }, [endpoint]);

  return { messages, input, setInput, streaming, error, send };
}
